import React, { useState, useEffect, useCallback } from 'react';
import {
  Form, Input, InputNumber, Select, Button, Card, Row, Col,
  message, Space, Divider, Typography, DatePicker,
} from 'antd';
import { SaveOutlined, ArrowLeftOutlined } from '@ant-design/icons';
import { useNavigate, useParams } from 'react-router-dom';
import dayjs from 'dayjs';
import api from '../../api/axios';

const { Title } = Typography;
const { Option } = Select;
const { TextArea } = Input;

const KiralikForm = () => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [iller, setIller] = useState([]);
  const [ilceler, setIlceler] = useState([]);
  const [emlakTipleri, setEmlakTipleri] = useState([]);
  const [kiraSureAdet, setKiraSureAdet] = useState(null);
  const [kiraSureBirim, setKiraSureBirim] = useState('Ay');
  const { id } = useParams();
  const navigate = useNavigate();
  const isEdit = Boolean(id);

  const fetchIlceler = async (plakaNo) => {
    if (!plakaNo) { setIlceler([]); return; }
    try {
      const res = await api.get(`/lookup/ilceler/${plakaNo}`);
      const arr = Array.isArray(res.data) ? res.data : res.data.data || [];
      setIlceler(arr);
    } catch { setIlceler([]); }
  };

  const calcBitisTarihi = (baslangic, adet, birim) => {
    if (!baslangic || !adet) return null;
    if (birim === 'Gün') return baslangic.add(adet, 'day');
    if (birim === 'Ay') return baslangic.add(adet, 'month');
    if (birim === 'Yıl') return baslangic.add(adet, 'year');
    return null;
  };

  // Tüm lookup'lar ve form verisi tek seferde yükleniyor.
  // React 18 bu fonksiyon içindeki tüm setState çağrılarını tek render'a toplar,
  // böylece Select hem seçeneklere hem değere aynı render'da sahip olur.
  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const requests = [
        api.get('/lookup/iller'),
        api.get('/lookup/emlaktipi'),
        ...(isEdit ? [api.get(`/kiralik/${id}`)] : []),
      ];
      const results = await Promise.allSettled(requests);
      const [illerRes, emlakRes, dataRes] = results;

      if (illerRes.status === 'fulfilled') {
        const arr = Array.isArray(illerRes.value.data) ? illerRes.value.data : illerRes.value.data.data || [];
        setIller(arr);
      }
      if (emlakRes.status === 'fulfilled') {
        const arr = Array.isArray(emlakRes.value.data) ? emlakRes.value.data : emlakRes.value.data.data || [];
        setEmlakTipleri(arr);
      }
      if (isEdit && dataRes) {
        if (dataRes.status === 'fulfilled') {
          const item = dataRes.value.data.data || dataRes.value.data;
          if (item.kiraTarihi) item.kiraTarihi = dayjs(item.kiraTarihi);
          if (item.kiraBitisTarihi) item.kiraBitisTarihi = dayjs(item.kiraBitisTarihi);
          if (item.kiraSure) {
            const parts = String(item.kiraSure).trim().split(' ');
            const adet = parseInt(parts[0]);
            const birim = parts[1] || 'Ay';
            if (!isNaN(adet)) {
              setKiraSureAdet(adet);
              setKiraSureBirim(birim);
            }
          }
          if (item.plakaNo) fetchIlceler(item.plakaNo);
          if (item.emlakTipiId) item.emlakTipiId = Number(item.emlakTipiId);
          form.setFieldsValue(item);
        } else {
          message.error('Kayıt yüklenemedi');
        }
      }
    } finally {
      setLoading(false);
    }
  }, [id, isEdit, form]);

  useEffect(() => { loadAll(); }, [loadAll]);

  const handleIlChange = (val) => {
    form.setFieldValue('ilceId', undefined);
    fetchIlceler(val);
  };

  const handleAylikTutarChange = (val) => {
    if (val) {
      form.setFieldValue('yillikTutar', val * 12);
    }
  };

  const handleKiraTarihiChange = (date) => {
    if (date && kiraSureAdet) {
      const bitis = calcBitisTarihi(date, kiraSureAdet, kiraSureBirim);
      if (bitis) form.setFieldValue('kiraBitisTarihi', bitis);
    }
  };

  const handleSureAdetChange = (val) => {
    setKiraSureAdet(val);
    const sureStr = val ? `${val} ${kiraSureBirim}` : '';
    form.setFieldValue('kiraSure', sureStr);
    const kiraTarihi = form.getFieldValue('kiraTarihi');
    if (kiraTarihi && val) {
      const bitis = calcBitisTarihi(kiraTarihi, val, kiraSureBirim);
      if (bitis) form.setFieldValue('kiraBitisTarihi', bitis);
    }
  };

  const handleSureBirimChange = (val) => {
    setKiraSureBirim(val);
    const sureStr = kiraSureAdet ? `${kiraSureAdet} ${val}` : '';
    form.setFieldValue('kiraSure', sureStr);
    const kiraTarihi = form.getFieldValue('kiraTarihi');
    if (kiraTarihi && kiraSureAdet) {
      const bitis = calcBitisTarihi(kiraTarihi, kiraSureAdet, val);
      if (bitis) form.setFieldValue('kiraBitisTarihi', bitis);
    }
  };

  const onFinish = async (values) => {
    setSaving(true);
    try {
      const payload = {
        ...values,
        kiraTarihi: values.kiraTarihi ? values.kiraTarihi.format('YYYY-MM-DD') : null,
        kiraBitisTarihi: values.kiraBitisTarihi ? values.kiraBitisTarihi.format('YYYY-MM-DD') : null,
      };
      if (isEdit) {
        await api.put(`/kiralik/${id}`, payload);
        message.success('Kiralık işlem güncellendi');
      } else {
        await api.post('/kiralik', payload);
        message.success('Kiralık işlem eklendi');
      }
      navigate('/kiralik');
    } catch (err) {
      message.error(err.response?.data?.message || 'Kayıt başarısız');
    } finally {
      setSaving(false);
    }
  };

  const sectionCardStyle = { marginBottom: 16, borderRadius: 8 };

  return (
    <div>
      <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
        <Col>
          <Space>
            <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/kiralik')}>Geri</Button>
            <Title level={4} style={{ margin: 0 }}>{isEdit ? 'Kiralık Düzenle' : 'Yeni Kiralık Ekle'}</Title>
          </Space>
        </Col>
      </Row>

      <Form form={form} layout="vertical" onFinish={onFinish}>
        {/* Mülk Bilgileri */}
        <Card title="Mülk Bilgileri" style={sectionCardStyle} loading={loading}>
          <Row gutter={16}>
            <Col xs={24} md={8}>
              <Form.Item name="emlakTipiId" label="Emlak Tipi" rules={[{ required: true, message: 'Zorunlu' }]}>
                <Select placeholder="Emlak tipi seçin" showSearch allowClear>
                  {emlakTipleri.map((t) => (
                    <Option key={t.emlakTipiId} value={Number(t.emlakTipiId)}>{t.emlakTipiAdi}</Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col xs={24} md={8}>
              <Form.Item name="plakaNo" label="İl">
                <Select
                  placeholder="İl seçin"
                  showSearch
                  allowClear
                  onChange={handleIlChange}
                  filterOption={(input, option) =>
                    (option?.children || '').toLowerCase().includes(input.toLowerCase())
                  }
                >
                  {iller.map((il) => (
                    <Option key={il.plakaNo} value={il.plakaNo}>
                      {il.ilAdi}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col xs={24} md={8}>
              <Form.Item name="ilceId" label="İlçe">
                <Select placeholder="İlçe seçin" showSearch allowClear disabled={!ilceler.length}>
                  {ilceler.map((ilce) => (
                    <Option key={ilce.ilceId} value={ilce.ilceId}>{ilce.ilceAdi}</Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col xs={24} md={12}>
              <Form.Item name="adres" label="Adres">
                <TextArea rows={2} placeholder="Tam adres" />
              </Form.Item>
            </Col>
            <Col xs={24} md={6}>
              <Form.Item name="kiraAmac" label="Kira Amaç">
                <Input placeholder="Konut / İşyeri" />
              </Form.Item>
            </Col>
            <Col xs={24} md={6}>
              <Form.Item name="kiraDemirbas" label="Demirbaş">
                <Input placeholder="Demirbaş bilgisi" />
              </Form.Item>
            </Col>
          </Row>
        </Card>

        {/* Kiraya Veren */}
        <Card title="Kiraya Veren Bilgileri" style={sectionCardStyle}>
          <Row gutter={16}>
            <Col xs={24} md={8}>
              <Form.Item name="kirayaVeren" label="Ad Soyad" rules={[{ required: true, message: 'Zorunlu' }]}>
                <Input placeholder="Kiraya veren adı soyadı" />
              </Form.Item>
            </Col>
            <Col xs={24} md={8}>
              <Form.Item name="kirayaVerenTc" label="TC Kimlik No">
                <Input placeholder="TC Kimlik No" maxLength={11} />
              </Form.Item>
            </Col>
            <Col xs={24} md={8}>
              <Form.Item name="kirayaVerenTel" label="Telefon">
                <Input placeholder="05xx xxx xxxx" />
              </Form.Item>
            </Col>
          </Row>
        </Card>

        {/* Kiralayan */}
        <Card title="Kiralayan Bilgileri" style={sectionCardStyle}>
          <Row gutter={16}>
            <Col xs={24} md={8}>
              <Form.Item name="kiralayanAdi" label="Ad Soyad" rules={[{ required: true, message: 'Zorunlu' }]}>
                <Input placeholder="Kiralayan adı soyadı" />
              </Form.Item>
            </Col>
            <Col xs={24} md={8}>
              <Form.Item name="kiralayanTc" label="TC Kimlik No">
                <Input placeholder="TC Kimlik No" maxLength={11} />
              </Form.Item>
            </Col>
            <Col xs={24} md={8}>
              <Form.Item name="kiralayanTel" label="Telefon">
                <Input placeholder="05xx xxx xxxx" />
              </Form.Item>
            </Col>
          </Row>
        </Card>

        {/* Ödeme Bilgileri */}
        <Card title="Ödeme Bilgileri" style={sectionCardStyle}>
          <Row gutter={16}>
            <Col xs={12} md={6}>
              <Form.Item name="aylikTutar" label="Aylık Tutar (₺)">
                <InputNumber
                  min={0}
                  style={{ width: '100%' }}
                  formatter={(v) => `${v}`.replace(/\B(?=(\d{3})+(?!\d))/g, '.')}
                  parser={(v) => v.replace(/\./g, '')}
                  onChange={handleAylikTutarChange}
                  placeholder="0"
                />
              </Form.Item>
            </Col>
            <Col xs={12} md={6}>
              <Form.Item name="yillikTutar" label="Yıllık Tutar (₺)">
                <InputNumber
                  min={0}
                  style={{ width: '100%' }}
                  formatter={(v) => `${v}`.replace(/\B(?=(\d{3})+(?!\d))/g, '.')}
                  parser={(v) => v.replace(/\./g, '')}
                  placeholder="Otomatik hesaplanır"
                />
              </Form.Item>
            </Col>
            <Col xs={12} md={6}>
              <Form.Item name="odemeSekli" label="Ödeme Şekli">
                <Select placeholder="Seçin" allowClear>
                  <Option value="Nakit">Nakit</Option>
                  <Option value="Havale">Havale</Option>
                  <Option value="EFT">EFT</Option>
                </Select>
              </Form.Item>
            </Col>
            <Col xs={12} md={6}>
              <Form.Item name="ibanNo" label="IBAN No">
                <Input placeholder="TR00 0000 0000 0000 0000 0000 00" />
              </Form.Item>
            </Col>
          </Row>
        </Card>

        {/* Kira Dönemi */}
        <Card title="Kira Dönemi" style={sectionCardStyle}>
          <Row gutter={16}>
            <Col xs={12} md={6}>
              <Form.Item name="kiraTarihi" label="Kira Başlangıç Tarihi">
                <DatePicker
                  style={{ width: '100%' }}
                  format="DD.MM.YYYY"
                  placeholder="Tarih seçin"
                  onChange={handleKiraTarihiChange}
                />
              </Form.Item>
            </Col>
            <Col xs={12} md={6}>
              <Form.Item label="Kira Süresi">
                <Space.Compact style={{ width: '100%' }}>
                  <InputNumber
                    value={kiraSureAdet}
                    onChange={handleSureAdetChange}
                    min={1}
                    max={999}
                    style={{ width: '60%' }}
                    placeholder="Süre"
                  />
                  <Select
                    value={kiraSureBirim}
                    onChange={handleSureBirimChange}
                    style={{ width: '40%' }}
                  >
                    <Option value="Gün">Gün</Option>
                    <Option value="Ay">Ay</Option>
                    <Option value="Yıl">Yıl</Option>
                  </Select>
                </Space.Compact>
              </Form.Item>
              <Form.Item name="kiraSure" hidden><Input /></Form.Item>
            </Col>
            <Col xs={12} md={6}>
              <Form.Item name="kiraBitisTarihi" label="Bitiş Tarihi">
                <DatePicker style={{ width: '100%' }} format="DD.MM.YYYY" placeholder="Otomatik hesaplanır" />
              </Form.Item>
            </Col>
            <Col xs={12} md={6}>
              <Form.Item name="kiralikDurum" label="Durum">
                <Select placeholder="Durum seçin" allowClear>
                  <Option value="Aktif">Aktif</Option>
                  <Option value="Bitti">Bitti</Option>
                  <Option value="Beklemede">Beklemede</Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>
        </Card>

        <Row justify="end" style={{ marginTop: 8 }}>
          <Space>
            <Button onClick={() => navigate('/kiralik')}>İptal</Button>
            <Button type="primary" htmlType="submit" loading={saving} icon={<SaveOutlined />}>
              {isEdit ? 'Güncelle' : 'Kaydet'}
            </Button>
          </Space>
        </Row>
      </Form>
    </div>
  );
};

export default KiralikForm;